/**
 * PATH: packages/ui/src/kanban/components/KanbanCardCompleteButton.tsx
 * FILE_ID: UI-KANBAN-CARD-COMPLETE-BTN-001
 * PURPOSE: The Trello/Linear-style "mark complete" check that sits in the
 *   card title's left gutter.
 * WHY:
 *   FEAT-2026-06-15-KANBAN-CARD-HOVER first put a completion circle in the
 *   card's bottom-right. Operator follow-up (2026-06-20): *"the check is
 *   not intuitional"* — bottom-right is an unusual spot for "done". Moved
 *   to a reserved checkbox column at the START of the title row (the
 *   universally-recognised todo/Trello position).
 *   Second follow-up (2026-06-20): *"the check should only be visible when
 *   you hover"* + the emerald-on-card-hover read as *already complete*. So:
 *     - rest (open card, not hovered): INVISIBLE (gutter still reserved so
 *       the title never reflows — only the paint is hidden, not the space);
 *     - card hovered (open card): an EMPTY NEUTRAL circle (a real "to-do"
 *       checkbox, not a green tick), which only previews emerald on its OWN
 *       hover so "click to complete" reads clearly;
 *     - DONE: a filled emerald tick, shown at all times (even un-hovered)
 *       so completion reads at a glance alongside the strikethrough title.
 * FLOW:
 *   props (done, visible, pending, onToggle) -> a circular toggle button.
 *   Click -> onToggle() (host flips status OPEN<->DONE), swallowing the
 *   event so the card's onOpen never fires.
 * DEPENDENCIES:
 *   - lucide-react Check: the tick glyph.
 * RELATED:
 *   - ./KanbanCard.tsx: renders this as the first child of the title row.
 *   - ./KanbanChecklistRow.tsx: the sibling tick used by checklist-mode
 *     lanes — same emerald visual language.
 */
import { Check } from 'lucide-react'

export interface KanbanCardCompleteButtonProps {
  /** True when the card's status is DONE — renders the filled tick. */
  done: boolean
  /** Card is hovered — reveals the empty checkbox circle (hidden at rest so
   *  the board stays clean). The gutter is always reserved, so this only
   *  fades the paint in/out; the title never reflows. Ignored when `done`
   *  (a completed tick is always shown). */
  visible: boolean
  /** Disable + show a wait cursor while the toggle mutation is in flight. */
  pending?: boolean
  /** Flip the card's complete state. The host wires this to the shared
   *  status mutation (OPEN <-> DONE). */
  onToggle: () => void
}

/** Title-gutter completion check. The 18px slot is always reserved (so the
 *  title never shifts), but the circle only PAINTS when the card is hovered
 *  or the card is DONE. Three states: rest = invisible, hover = empty
 *  neutral checkbox (emerald preview on its own hover), done = filled
 *  emerald tick. Pure presentational — all state lives in the parent
 *  KanbanCard. */
export function KanbanCardCompleteButton({
  done,
  visible,
  pending,
  onToggle,
}: KanbanCardCompleteButtonProps) {
  return (
    <button
      type="button"
      data-card-complete-btn=""
      aria-label={done ? 'Mark as not complete' : 'Mark as complete'}
      aria-pressed={done}
      title={done ? 'Completed — click to reopen' : 'Mark complete'}
      disabled={pending}
      // Swallow pointer + dblclick so toggling never opens the drawer or
      // starts an inline rename / drag.
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        if (!pending) onToggle()
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      className={[
        'mt-[1px] inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-all duration-150',
        // Fill / accent by state. An open card shows an EMPTY neutral
        // checkbox (no green tick) so a hovered card never reads as already
        // complete; emerald only previews on the circle's own hover.
        done
          ? 'border-emerald-500 bg-emerald-500 text-white shadow-sm'
          : 'border-neutral-300 text-transparent hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-600 dark:border-zinc-500 dark:hover:bg-emerald-500/10',
        // Visibility: hidden at rest (reserved gutter → no reflow), painted
        // only on card hover or when DONE.
        done || visible ? 'opacity-100' : 'opacity-0 pointer-events-none',
        pending ? 'cursor-wait' : 'cursor-pointer',
      ].join(' ')}
    >
      <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
    </button>
  )
}
